import { ClickLoadingIndicator, FullScreenDevicePreview } from '../../components';
import type { DevicePreview, DevicePreviewClient } from '../../types/device-preview';
import type { LitmusDeviceIds } from 'src/services/device-casings/LitmusDeviceIds';
import React, { useState } from 'react';
import ThumbnailCard from '../../components/Thumbnail/ThumbnailCard';
import { getDeviceDetails } from '../../services/device-casings/DeviceDetails';

interface EmailClientsProps {
  clients: DevicePreview['emailClients'] | [];
  group: string | undefined;
  loading: boolean;
}

const EmailClients: React.FC<EmailClientsProps> = ({ clients, group }) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState<DevicePreviewClient | null>(null);

  const isMobile = /mobile|tablet/i.test(group ?? '');
  const onThumbnailClick = (item: DevicePreviewClient) => {
    setPreviewItem(item);
    setIsPreviewOpen(true);
  };

  const onFullscreenClose = () => {
    setIsPreviewOpen(false);
    setPreviewItem(null);
  };

  return (
    <>
      {isPreviewOpen && previewItem && (
        <FullScreenDevicePreview
          name={`${previewItem.name}, ${previewItem.platform}`}
          fullUrl={previewItem.full}
          onClose={onFullscreenClose}
          deviceDetails={isMobile ? getDeviceDetails(previewItem.id as LitmusDeviceIds) : undefined}
        />
      )}
      <li className="bg-neutral-95 p-[9px] -ml-[10px]">
        <div className="relative">
          <div className="-mt-[15px]">
            <h2
              data-testid="platform-group"
              className="font-sans text-cds-3 font-normal mb-4 text-left text-cds-on-surface-3 ml-[20px] mt-[20px] inbox-preview-heading"
            >
              {group ? group.replace(/\b\w/g, (c) => c.toUpperCase()) : ''} Email Clients
            </h2>
            <ul className="inline-flex items-start content-start flex-wrap gap-y-[16px] gap-x-[20px] ml-[13px] mt-[5px]">
              {clients.map((item) => (
                <li
                  data-testid="email-client"
                  key={item.id}
                  className="m-0"
                >
                  <ThumbnailCard
                    onClick={() => onThumbnailClick(item)}
                    thumbnailUrl={item.thumb}
                    text={`${item.name}, ${item.platform}`}
                    showLoader={!isPreviewOpen}
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </li>
    </>
  );
};

export const InboxPreview: React.FC<{ devicePreviews: DevicePreview | null; loading: boolean }> = ({
  devicePreviews,
  loading,
}) => {
  const groupedClients = devicePreviews?.emailClients.reduce(
    (groups, item) => {
      const group = item.group || 'Other';
      if (!groups[group]) groups[group] = [];
      groups[group]?.push(item);
      return groups;
    },
    {} as Record<string, DevicePreview['emailClients']>,
  );

  const hasClients = groupedClients && Object.keys(groupedClients).length > 0;

  return (
    <div className="device-preview-container bg-neutral-95 p-[10px] -mt-[3px] -ml-[10px] -mr-[10px] min-h-[80vh] flex flex-col inbox-preview-bg">
      <ClickLoadingIndicator
        isLoading={loading}
        type="page"
      />
      {hasClients ? (
        <ul className="inbox-preview-group-list">
          {Object.entries(groupedClients).map(([group, clients]) => (
            <EmailClients
              key={group}
              clients={clients}
              group={group}
              loading={loading}
            />
          ))}
        </ul>
      ) : !loading ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="no-previews-empty-state">No previews found</div>
        </div>
      ) : null}
    </div>
  );
};
